/**
 * چاپِ اموال — لیبلِ بارکددار برای چسباندن روی هر قلم، و چاپِ لیست.
 * هر دو در پنجرهٔ تازه با HTMLِ مستقل ساخته می‌شوند (فونتِ Peyda از همین سایت) و
 * پنجرهٔ چاپِ مرورگر خودکار باز می‌شود؛ کاربر می‌تواند PDF هم بگیرد.
 */
import { code128Svg } from "./code128";
import { toJalali } from "./dateUtils";

export const LABEL_SIZES = [
  { value: "50x25", label: "۵۰×۲۵ میلی‌متر (کوچک)", w: 50, h: 25 },
  { value: "60x40", label: "۶۰×۴۰ میلی‌متر", w: 60, h: 40 },
  { value: "80x50", label: "۸۰×۵۰ میلی‌متر (بزرگ)", w: 80, h: 50 },
  { value: "a4", label: "برگهٔ A4 — ۲۴ تایی (۳×۸)", w: 70, h: 37.1, sheet: true },
];

export const LABEL_FIELDS = [
  { value: "title", label: "عنوان (نامِ مجموعه)" },
  { value: "name", label: "نامِ اموال" },
  { value: "barcode", label: "بارکدِ کد" },
  { value: "code", label: "کدِ اموال" },
  { value: "serial_no", label: "سریال" },
  { value: "owner", label: "مالک" },
  { value: "quantity", label: "تعداد" },
  { value: "date", label: "تاریخ (خرید/ورود)" },
  { value: "location", label: "محل نگهداری" },
  { value: "custodian", label: "تحویل‌گیرنده" },
  { value: "category", label: "دسته" },
];

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[c]));
const fa = (v) => String(v ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

export const ownerText = (r) => (r.ownership === "custody"
  ? `امانی — ${r.owner_name || "؟"}` : (r.owner_name || "خودمان"));

function fontCss() {
  const base = `${window.location.origin}${process.env.PUBLIC_URL || ""}/fonts`;
  return `
@font-face{font-family:'Peyda';src:url('${base}/PeydaWeb-Regular.woff') format('woff');font-weight:400}
@font-face{font-family:'Peyda';src:url('${base}/PeydaWeb-Bold.woff') format('woff');font-weight:700}
*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
html,body{margin:0;padding:0;direction:rtl;font-family:'Peyda',Tahoma,sans-serif;color:#000;background:#fff}`;
}

function openAndPrint(title, css, body) {
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(`<!doctype html><html lang="fa" dir="rtl"><head><meta charset="utf-8">
<title>${esc(title)}</title><style>${fontCss()}${css}</style></head><body>${body}
<script>
  (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve())
    .then(function(){ setTimeout(function(){ window.focus(); window.print(); }, 150); });
</script></body></html>`);
  w.document.close();
  return true;
}

function labelHtml(r, fields, title, copyNo, copies) {
  const has = (f) => fields.includes(f);
  const date = r.purchase_date || r.entry_date;
  const rows = [];
  if (has("serial_no") && r.serial_no) rows.push(["سریال", esc(r.serial_no)]);
  if (has("owner")) rows.push(["مالک", esc(ownerText(r))]);
  if (has("quantity")) {
    rows.push(["تعداد", copies > 1 ? `${fa(copyNo)} از ${fa(copies)}` : fa(r.quantity ?? 1)]);
  }
  if (has("date") && date) rows.push([r.purchase_date ? "تاریخ خرید" : "تاریخ ورود", fa(toJalali(date))]);
  if (has("location") && r.location) rows.push(["محل", esc(r.location)]);
  if (has("custodian") && r.custodian) rows.push(["تحویل", esc(r.custodian)]);
  if (has("category") && r.category) rows.push(["دسته", esc(r.category)]);
  const bc = has("barcode") ? code128Svg(r.code) : null;
  return `<div class="lbl">
    ${has("title") && title ? `<div class="t">${esc(title)}</div>` : ""}
    ${has("name") ? `<div class="n">${esc(r.name)}</div>` : ""}
    ${bc ? `<div class="bc">${bc}</div>` : ""}
    ${has("code") || (has("barcode") && !bc) ? `<div class="c">${esc(r.code)}</div>` : ""}
    ${rows.length ? `<div class="kv">${rows.map(([k, v]) => `<span><b>${k}:</b> <bdi>${v}</bdi></span>`).join("")}</div>` : ""}
  </div>`;
}

/**
 * @param rows ردیف‌های اموال
 * @param opts { size, fields[], title, perQuantity }
 */
export function printAssetLabels(rows, { size = "60x40", fields, title = "", perQuantity = false } = {}) {
  const sz = LABEL_SIZES.find((s) => s.value === size) || LABEL_SIZES[1];
  const flds = fields && fields.length ? fields : LABEL_FIELDS.map((f) => f.value);
  const small = sz.h <= 25;
  const items = [];
  rows.forEach((r) => {
    const n = perQuantity ? Math.max(1, Math.min(500, Number(r.quantity) || 1)) : 1;
    for (let i = 1; i <= n; i++) items.push(labelHtml(r, flds, title, i, n));
  });
  const base = small ? 2.3 : sz.h <= 40 ? 2.7 : 3.1;   // mm
  const css = `
@page{size:${sz.sheet ? "A4" : `${sz.w}mm ${sz.h}mm`};margin:0}
.sheet{${sz.sheet ? "display:grid;grid-template-columns:repeat(3,70mm);grid-auto-rows:37.1mm;padding:0;width:210mm" : ""}}
.lbl{width:${sz.w}mm;height:${sz.h}mm;padding:${small ? 1.2 : 2}mm ${small ? 1.5 : 2.5}mm;overflow:hidden;
  display:flex;flex-direction:column;justify-content:center;gap:${small ? 0.3 : 0.6}mm;
  ${sz.sheet ? "border:0.2mm dashed #bbb;" : "page-break-after:always;break-after:page;"}}
.t{font-size:${base * 0.85}mm;font-weight:700;text-align:center;border-bottom:0.2mm solid #000;padding-bottom:0.4mm}
.n{font-size:${base * 1.15}mm;font-weight:700;text-align:center;line-height:1.25;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bc{height:${small ? 6 : sz.h <= 40 ? 9 : 12}mm;direction:ltr}
.bc svg{width:100%;height:100%;display:block}
.c{font-size:${base}mm;text-align:center;direction:ltr;letter-spacing:0.3mm;font-weight:700}
.kv{display:flex;flex-wrap:wrap;gap:0.3mm ${small ? 1.5 : 2.5}mm;font-size:${base * 0.82}mm;line-height:1.3}
.kv b{font-weight:700}
@media screen{body{background:#eee;padding:10px}.lbl{background:#fff;margin:0 auto 8px;outline:1px solid #ccc}
  .sheet .lbl{margin:0;outline:none}.sheet{background:#fff;margin:0 auto}}`;
  return openAndPrint(`لیبل اموال (${items.length})`, css, `<div class="sheet">${items.join("")}</div>`);
}

/**
 * چاپِ لیست — ستون‌ها: [{title, get(row) → متن, num?}]
 */
export function printAssetList(rows, cols, { title = "لیست اموال", subtitle = "", footer = [] } = {}) {
  const head = `<tr><th>ردیف</th>${cols.map((c) => `<th>${esc(c.title)}</th>`).join("")}</tr>`;
  const body = rows.map((r, i) => `<tr><td>${fa(i + 1)}</td>${cols.map((c) => {
    const v = c.get(r);
    return `<td class="${c.num ? "num" : ""}">${c.num ? fa(esc(v)) : esc(v)}</td>`;
  }).join("")}</tr>`).join("");
  const css = `
@page{size:A4 landscape;margin:10mm}
h1{font-size:16px;margin:0 0 2px}.sub{font-size:11px;color:#444;margin-bottom:8px}
table{width:100%;border-collapse:collapse;font-size:10.5px}
th,td{border:1px solid #777;padding:3px 5px;text-align:right;vertical-align:top}
th{background:#e8edf5;font-weight:700}
thead{display:table-header-group}tr{break-inside:avoid}
td.num{text-align:left;direction:ltr;white-space:nowrap}
.foot{margin-top:8px;font-size:11.5px;display:flex;gap:24px;flex-wrap:wrap}`;
  const now = new Date().toISOString();
  return openAndPrint(title, css, `
<h1>${esc(title)}</h1>
<div class="sub">${esc(subtitle)}${subtitle ? " — " : ""}تاریخ چاپ: ${fa(toJalali(now))} — ${fa(rows.length)} ردیف</div>
<table><thead>${head}</thead><tbody>${body}</tbody></table>
${footer.length ? `<div class="foot">${footer.map(([k, v]) => `<span><b>${esc(k)}:</b> ${fa(esc(v))}</span>`).join("")}</div>` : ""}`);
}
